import { Box, Dialog, DialogContent, DialogProps } from '@mui/material'
import { FormProvider } from 'react-hook-form'
import { EditModeProvider } from 'contexts/edit-mode.context'
import { useDispatch, useSelector } from 'react-redux'

import {
  addDropTrailerTask,
  setOrderEventId,
} from 'store/trips/trips.slice'

import { useDropAddressForm } from '../forms/drop-address-form/drop-address-form.hooks'
import DropAddressForm from '../forms/drop-address-form/drop-address-form.component'
import ActionButtons from '../action-buttons/action-buttons.component'
import { useLazyGetOrderByIdQuery } from 'store/orders/orders.api'
import { RootState } from 'store'
import {
  buildDropAddress,
  DropAddressFormData,
} from './drop-form-dialog.utils'

type DropFormDialogProps = {
  handleClose: () => void
} & DialogProps

const DropFormDialog = ({ open, handleClose }: DropFormDialogProps) => {
  const dispatch = useDispatch()
  const { formMethods } = useDropAddressForm()

  const { orderId, orderEventId, tripsPayload } = useSelector(
    (state: RootState) => state.trips
  )

  const [getOrderById] = useLazyGetOrderByIdQuery()

  const handleDialogClose = () => {
    dispatch(setOrderEventId({ orderEventId: 0 }))
    handleClose()
  }

  const handleSave = async (data: DropAddressFormData) => {
    const address = buildDropAddress(data)
    const removeDeliveryOrderEventId = orderEventId || undefined

    try {
      const relatedTask = tripsPayload.tripTasks.find(
        (task) =>
          task.orderEventId === removeDeliveryOrderEventId ||
          (task.type === 'DROP_TRAILER' &&
            task.sourceOrderPkId != null &&
            task.orderEventId == null)
      )

      let sourceOrderPkId = relatedTask?.sourceOrderPkId ?? (orderId || undefined)
      let userOrderId = relatedTask?.orderId

      if ((!userOrderId || !sourceOrderPkId) && orderId) {
        const currentOrder = await getOrderById({ orderId }).unwrap()
        sourceOrderPkId = currentOrder.orderId
        userOrderId = currentOrder.userOrderId
      }

      if (!sourceOrderPkId || !userOrderId) {
        throw new Error('Order id is required to enter a drop trailer address')
      }

      dispatch(
        addDropTrailerTask({
          address,
          orderId: userOrderId,
          sourceOrderPkId,
          removeDeliveryOrderEventId,
        })
      )

      handleDialogClose()
    } catch (error) {
      console.error('Error while adding drop trailer task', error)
    }
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm">
      <DialogContent sx={{ p: 0 }}>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(handleSave)}>
            <Box p={3}>
              <EditModeProvider initialEditMode suppressAutofill>
                <DropAddressForm />
              </EditModeProvider>
            </Box>

            <Box mt={2}>
              <ActionButtons
                firstBtnProps={{
                  title: 'Cancel',
                  onClick: handleDialogClose,
                  bgcolor: 'secondary.main',
                }}
                secondBtnProps={{
                  title: 'Save',
                  onClick: formMethods.handleSubmit(handleSave),
                }}
              />
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default DropFormDialog
